import { inject, injectable } from 'tsyringe';
import { LoggerService } from '../../logger.service';
import {
  conditionType,
  ConditionType,
} from './decorators/condition-type.decorator';
import { ConditionContext } from './interfaces/condition-context.interface';
import { ICondition, MatchResult } from './interfaces/condition.interface';
import {
  kDIConfigurations,
  kDITGameEventsListener,
  kDITOWWrapper,
} from '../../../constants/injectionTokens';
import { IOWWrapper } from '../../../interfaces/ow-wrappers/ow-wrapper.interface';
import {
  IOWGameEventsListener,
  IOWGamesEventsDelegate,
} from '../../../interfaces/ow-wrappers/ow-game-events-listener-wrapper.interface';
import { NoRecommendationsReasons } from '../no-recommendations-reasons.enums';
import { PromiseResolver } from '../../../utils/promise-resolver';
import { IGameEvent } from './interfaces/game-event-trigger.model';
import { Application } from '../../../models/application.model';
import { IConfigurations } from '../../../interfaces/configurations.interface';

const kGameEventTimeoutMs = 1000 * 60 * 1;
const timesToRunTimer = 1;

// -----------------------------------------------------------------------------
@injectable()
@conditionType(ConditionType.GameEvent)
export class ConditionGameInfoReceived
  implements ICondition, IOWGamesEventsDelegate
{
  private readonly logger = LoggerService.getCategory(
    'ConditionGameInfoReceived',
  );

  private promiseResolver: PromiseResolver;
  private waitingForEvents = false;
  private triggerEvent: IGameEvent;
  private decisionEvent: IGameEvent;
  private triggered = false;
  private foundDecisionEvent = false;
  private infoDB: any = null;
  private timeOutTimer: NodeJS.Timeout = null;
  private timedOut = false;
  private timerRuns = 0;

  // ---------------------------------------------------------------------------
  constructor(
    @inject(kDITOWWrapper)
    private readonly owWrapper: IOWWrapper,
    @inject(kDITGameEventsListener)
    private readonly gameEventListener: IOWGameEventsListener,
    @inject(kDIConfigurations)
    private readonly configurations: IConfigurations,
  ) {}

  // ---------------------------------------------------------------------------
  // ICondition
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public get id(): string {
    return this.logger.name;
  }

  // ---------------------------------------------------------------------------
  public async shouldSkip(context: ConditionContext) {
    const { game, application } = context;
    this.logger.info(
      'shouldSkip check for game:',
      game?.id,
      'app:',
      application?.uid,
    );

    if (!game?.useGameTriggers) {
      this.logger.info('Skipping because game triggers are disabled');
      return true;
    }

    if (!application?.gameEventTrigger) {
      this.logger.info(
        'Skipping because application has no game event trigger',
      );
      return true;
    }

    if (application.gameEventTrigger.eventSource !== 'LEP') {
      this.logger.info(
        'Skipping because application has no launcher event triggers',
      );
      return true;
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  async match(context: ConditionContext): Promise<MatchResult> {
    try {
      this.foundDecisionEvent = false;
      if (this.timerRuns >= timesToRunTimer) {
        this.logger.info('Timer has already run the maximum number of times');
        return [false, NoRecommendationsReasons.GameEventsTimeout];
      }

      const { application } = context;
      if (this.owWrapper.getPlatform() === 'owe') {
        return [false, NoRecommendationsReasons.GameEventsPlatformNotSupported];
      }

      this.triggerEvent = application?.gameEventTrigger?.triggerEvent;
      this.decisionEvent = application?.gameEventTrigger?.decisionEvent;
      const handleResult = await this.evaluateInfoUpdate();
      if (this.triggered && handleResult) {
        return [true];
      }

      await this.awaitEvents(application);
      this.logger.info('timeout status', this.timedOut);
      if (this.timedOut) {
        return [false, NoRecommendationsReasons.GameEventsTimeout];
      }

      if (!this.foundDecisionEvent) {
        return [false, NoRecommendationsReasons.GameEventWrongValue];
      }

      this.ClearTimeout();
      return [true];
    } catch (error) {
      this.logger.error('Error matching game events', error);
      return [false, NoRecommendationsReasons.GameEventsNotTriggered];
    }
  }

  // ---------------------------------------------------------------------------
  private async awaitEvents(application: Application) {
    this.startTimer();
    await this.gameEventListener?.start([application], this);
    this.waitingForEvents = true;
    this.promiseResolver = new PromiseResolver();
    this.logger.info('Waiting for Info updates');
    await this.promiseResolver?.promise();

    this.waitingForEvents = false;
    this.gameEventListener?.stop();
  }

  // ---------------------------------------------------------------------------
  private startTimer() {
    if (this.timerRuns >= timesToRunTimer) {
      return;
    }

    const ttl =
      this.configurations?.generalConfiguration?.GameEventTimerInSeconds *
        1000 || kGameEventTimeoutMs;

    this.timeOutTimer = setTimeout(() => {
      this.timerRuns++;
      this.timedOut = true;
      this.logger.info('Game events info timed out triggered');
      this.promiseResolver?.resolve();
    }, ttl);
  }

  // ---------------------------------------------------------------------------
  private async setToInfoDB(info: any) {
    let tempDB = { ...this.infoDB };
    if (!info) {
      return;
    }

    // eslint-disable-next-line guard-for-in
    for (const i in info) {
      tempDB = { ...tempDB, [i]: { ...tempDB[i], ...info[i] } };
    }

    this.infoDB = tempDB;
  }

  // ---------------------------------------------------------------------------
  private async evaluateInfoUpdate() {
    try {
      const foundTriggersEvents = this.triggered
        ? true
        : this.triggerEvent?.values?.includes(
            this.infoDB?.[this.triggerEvent?.featureName]?.[
              this.triggerEvent?.key
            ],
          );

      if (!foundTriggersEvents) {
        return false;
      }

      this.logger.info('Found Trigger Event in Info update');

      this.triggered = true;
      const decisionEventValueSet =
        !!this.infoDB?.[this.decisionEvent.featureName]?.[
          this.decisionEvent.key
        ];

      this.logger.info('Decision event value set:', decisionEventValueSet);

      const foundDecisionEvent = this.decisionEvent.values.includes(
        this.infoDB?.[this.decisionEvent.featureName]?.[this.decisionEvent.key],
      );

      if (decisionEventValueSet && !foundDecisionEvent) {
        this.logger.info(
          'Decision event value not matching, denying recommendation',
        );
        this.finishAwaitingEvents();
        return false;
      }

      if (foundTriggersEvents && decisionEventValueSet && foundDecisionEvent) {
        this.logger.info('Found Trigger, allowing recommendation');
        this.foundDecisionEvent = true;
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  private ClearTimeout() {
    if (!this.timeOutTimer) {
      return;
    }

    clearTimeout(this.timeOutTimer);
  }

  // ---------------------------------------------------------------------------
  private finishAwaitingEvents() {
    this.ClearTimeout();

    this.promiseResolver.resolve();
  }

  // ---------------------------------------------------------------------------
  // OWGamesEventsDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onInfoUpdates(info: any) {
    if (!this.waitingForEvents) {
      return;
    }

    // this.logger.info('LEP Info update:', JSON.stringify(info));
    this.setToInfoDB(info);

    const handled = await this.evaluateInfoUpdate();
    if (!handled) {
      return;
    }

    this.finishAwaitingEvents();
  }

  // ---------------------------------------------------------------------------
  onNewEvents(e: any) {
    // Not implemented
  }
}
